import { Calendar, Tag, UserPlus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/shared/config/routes';
import { useEscapeKey } from '@/shared/hooks';

/** The three things a master most often creates from a phone. */
const ACTIONS = [
  {
    to: `${ROUTES.app.calendar}?new=true`,
    icon: Calendar,
    titleKey: 'quickActions.newBooking',
    hintKey: 'quickActions.newBookingHint',
    tone: 'bg-purple-50 text-primary',
  },
  {
    to: ROUTES.app.clients,
    icon: UserPlus,
    titleKey: 'quickActions.newClient',
    hintKey: 'quickActions.newClientHint',
    tone: 'bg-blue-50 text-blue-600',
  },
  {
    to: ROUTES.app.services,
    icon: Tag,
    titleKey: 'quickActions.newService',
    hintKey: 'quickActions.newServiceHint',
    tone: 'bg-pink-50 text-pink-600',
  },
];

type QuickActionSheetProps = {
  open: boolean;
  onClose: () => void;
};

/** Bottom sheet behind the mobile "+" button. */
export const QuickActionSheet = ({ open, onClose }: QuickActionSheetProps) => {
  const { t } = useTranslation('app');
  const navigate = useNavigate();

  useEscapeKey(onClose, open);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[45] bg-black/20 backdrop-blur-sm md:hidden"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t('quickActions.open')}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className="fixed right-4 bottom-[72px] left-4 z-[45] flex flex-col overflow-hidden rounded-3xl bg-white p-2 shadow-2xl md:hidden"
          >
            {ACTIONS.map(({ to, icon: Icon, titleKey, hintKey, tone }) => (
              <button
                key={to}
                type="button"
                onClick={() => {
                  void navigate(to);
                  onClose();
                }}
                className="flex items-center gap-4 rounded-2xl p-4 text-left transition-colors hover:bg-gray-50"
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone}`}
                >
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{t(titleKey)}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{t(hintKey)}</p>
                </div>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
